import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ReCaptchaV3Service} from 'ngx-captcha';

import {Subscription} from 'rxjs';
import {QuestionsService} from 'src/app/services/questions.service';
import {LoaderService} from '../../../services/loader.service';
import {UrlBuilderService} from '../../../services/url-builder.service';
import {UpdateInfoPayload} from '../../../types/UpdateInfoPayload';
import {ToolsService} from '../../../services/tools.service';
import {ModalsControlService} from '../../../services/modals-control.service';
import {ModalCommand} from '../../../types/ModalCommand';
import {ModalTypeEnum} from '../../../enums/ModalEnums';
import {InfoModalContent} from '../../../types/InfoModalContent';
import {finalize} from 'rxjs/operators';
import {LoaderQueueItem} from '../../../types/LoaderQueueItem';
import {AppConfigService} from '../../../services/app-config.service';

@Component({
  selector: 'app-update-info-form',
  templateUrl: './update-info-form.component.html',
  styleUrls: ['./update-info-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpdateInfoFormComponent implements OnInit, OnDestroy, AfterViewInit {
  public askForm: UntypedFormGroup;
  public siteKey: string;
  private subscriptions: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<UpdateInfoFormComponent>,
    @Inject(MAT_DIALOG_DATA) public careProviderUnitId: number,
    private formBuilder: UntypedFormBuilder,
    private loaderService: LoaderService,
    private questionsService: QuestionsService,
    private urlBuilder: UrlBuilderService,
    private tools: ToolsService,
    private modalControl: ModalsControlService,
    private reCaptchaV3Service: ReCaptchaV3Service,
    private appConfigService: AppConfigService,
    private _change: ChangeDetectorRef,
  ) {
    this.siteKey = appConfigService.appConfig.recaptchaSiteKey;
  }

  ngOnInit(): void {
    this.buildAskForm();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngAfterViewInit(): void {
    this.tools.recaptchaFix();
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  saveRequest(): void {
    this.reCaptchaV3Service.execute(
      this.siteKey,
      'update_info',
      (token) => {
        this.askForm.controls.recaptcha.setValue(token || '');

        (Object as any).values(this.askForm.controls).forEach((control) => control.markAllAsTouched());
        if (this.askForm.valid) {
          const queryId: number = this.loaderService.generateQueueId();
          const queryItem: LoaderQueueItem = {
            id: queryId,
            title: '',
            description: '',
          };
          this.loaderService.addToQueue(queryItem);
          this.subscriptions.push(
            this.questionsService
              .UpdateInfo(this.getUpdateInfoObjectFromFormData())
              .pipe(
                finalize(() => {
                  this.loaderService.removeFromQueue(queryId);
                }),
              )
              .subscribe(
                (result) => this.processUpdateInfoData(result),
                (error) => console.error(error),
              ),
          );
        }
      },
      {
        useGlobalDomain: false,
      },
      (error) => console.log(error),
    );
  }

  private processUpdateInfoData(result: UpdateInfoPayload): void {
    this.openInfoModal();
    this._change.markForCheck();
    this.dialogRef.close();
  }

  private openInfoModal(): void {
    const infoContent: InfoModalContent = {
      title: 'Tack!',
      message: '<p>Vi har tagit emot ditt meddelande.</p><p>Vänligen,<br>Vården.se</p>',
    };
    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.INFO,
      data: infoContent,
    };
    this.modalControl.openModal(modalCommand);
  }

  private buildAskForm(): void {
    this.askForm = this.formBuilder.group({
      email: ['', [Validators.email]],
      message: ['', [Validators.required]],
      recaptcha: ['', [Validators.required]],
    });
  }

  private getUpdateInfoObjectFromFormData(): UpdateInfoPayload {
    const payload: UpdateInfoPayload = {
      careProviderId: this.careProviderUnitId,
      message: this.askForm.value.message,
      email: this.askForm.value.email,
      'g-recaptcha-response': this.askForm.value.recaptcha,
    };
    return payload;
  }
}
